"use client";

import Link from "next/link";
import Image from "next/image";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useTranslation } from "@/lib/i18n";
import type { BlogPostFull } from "@/lib/blog";

const AUTHOR = "Martin Haintz";

function BlogImage({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return (
    <figure className="my-8">
      <div className="relative w-full overflow-hidden border border-border-faint">
        <Image
          src={src}
          alt={alt}
          width={800}
          height={450}
          className="w-full h-auto object-cover"
          sizes="(max-width: 768px) 100vw, 800px"
        />
      </div>
      {caption && (
        <figcaption className="mt-2 text-center text-[11px] font-mono text-text-faintest uppercase tracking-widest">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function MdxImg({ src, alt }: { src?: string; alt?: string }) {
  if (!src) return null;
  return <BlogImage src={src} alt={alt ?? ""} />;
}

// Props as they arrive from react-markdown / rehype-raw: everything is an
// optional string, including the attributes of the custom <blogimage> element.
type MarkdownImageProps = {
  src?: string;
  alt?: string;
  caption?: string;
};

const markdownComponents = {
  blogimage: (props: MarkdownImageProps) => (
    <BlogImage src={props.src ?? ""} alt={props.alt ?? ""} caption={props.caption} />
  ),
  img: (props: MarkdownImageProps) => <MdxImg src={props.src} alt={props.alt} />,
};

export default function BlogPostView({ post }: { post: BlogPostFull }) {
  const { t, locale } = useTranslation();

  const title = locale === "en" && post.titleEn ? post.titleEn : post.title;
  const category = locale === "en" && post.categoryEn ? post.categoryEn : post.category;

  return (
    <main className="min-h-screen pt-32 pb-20 px-6">
      <article className="max-w-3xl mx-auto">
        {/* Back */}
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 mb-12 text-mint-text text-xs font-mono uppercase tracking-widest hover:underline"
        >
          &larr; {t("blog.backToBlog")}
        </Link>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-mint-text font-mono text-[10px] tracking-[0.2em] uppercase border border-mint-text/30 px-2 py-1">
            {category}
          </span>
          <span className="text-text-faintest font-mono text-[10px] uppercase tracking-widest">
            {post.readingTime}
          </span>
          <span className="text-text-faintest font-mono text-[10px] uppercase tracking-widest">
            {new Date(post.date).toLocaleDateString(locale === "de" ? "de-AT" : "en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl font-heading font-black text-text-primary leading-[0.95] tracking-tighter uppercase mb-10">
          {title}
        </h1>

        {/* Hero image */}
        {post.coverImage && (
          <div className="relative w-full overflow-hidden border border-border-faint mb-12">
            <Image
              src={post.coverImage}
              alt={post.coverImageAlt ?? title}
              width={1200}
              height={670}
              className="w-full h-auto"
              sizes="(max-width: 768px) 100vw, 800px"
              priority
            />
            {post.coverImageAiGenerated && (
              <div className="absolute bottom-3 right-3 text-[9px] font-mono text-white/80 uppercase tracking-widest bg-black/50 px-2 py-1">
                {t("common.aiGeneratedImage")}
              </div>
            )}
          </div>
        )}

        {/* MDX Content */}
        <div className="prose-blog">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents as unknown as Components}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* Author Card */}
        <div className="mt-16 pt-8 border-t border-border-faint">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 border border-border-subtle overflow-hidden relative shrink-0">
              <Image
                src="/photos/DSC00419_edited_martin_cropped.webp"
                alt={AUTHOR}
                fill
                className="object-cover object-top"
                sizes="56px"
              />
            </div>
            <div>
              <div className="text-[10px] font-mono text-text-faintest uppercase tracking-widest mb-1">
                {t("blog.author.label")}
              </div>
              <div className="text-sm font-bold text-text-primary">
                {AUTHOR}
              </div>
              <div className="text-[10px] font-mono text-mint-text uppercase tracking-widest">
                {t("blog.author.role")}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Link
            href="/blog"
            className="text-mint-text text-xs font-mono uppercase tracking-widest hover:underline"
          >
            &larr; {t("blog.backToAll")}
          </Link>
          <Link
            href="/#contact"
            className="px-6 py-3 bg-mint text-black text-xs font-black tracking-tight hover:bg-mint-hover transition-all uppercase"
          >
            {t("blog.requestProject")}
          </Link>
        </div>
      </article>
    </main>
  );
}
